import { useEffect } from 'react';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { useUpdatePriceSeries } from '../hooks';
import {
  priceSeriesFormSchema,
  type PriceSeries,
  type PriceSeriesFormValues,
} from '../schema';

interface Props {
  open: boolean;
  row: PriceSeries | null;
  onClose: () => void;
}

function buildDefaults(row: PriceSeries | null): PriceSeriesFormValues {
  return {
    isActive: row?.isActive ?? true,
    isManualMarketPrice: row?.isManualMarketPrice ?? false,
    runPredictionPipeline: row?.runPredictionPipeline ?? false,
    showPredictionInApp: row?.showPredictionInApp ?? false,
    notes: row?.notes ?? '',
  };
}

interface ToggleRowProps {
  label: string;
  helper: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ label, helper, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div
      className={`rounded-lg border border-border px-3 py-2 ${
        disabled ? 'bg-surface-muted/20 opacity-60' : 'bg-surface-muted/40'
      }`}
    >
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
            disabled={disabled}
          />
        }
        label={<span className="text-[13.5px] font-semibold text-foreground">{label}</span>}
        sx={{ ml: -1, mb: 0 }}
      />
      <p className="mt-0.5 pl-9 text-[12px] leading-snug text-foreground-muted">
        {helper}
      </p>
    </div>
  );
}

export function PriceSeriesEditDialog({ open, row, onClose }: Props) {
  const updateMutation = useUpdatePriceSeries();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PriceSeriesFormValues>({
    resolver: zodResolver(priceSeriesFormSchema) as Resolver<PriceSeriesFormValues>,
    defaultValues: buildDefaults(null),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !row) return;
    reset(buildDefaults(row));
  }, [open, row, reset]);

  // The DB CHECK (chk_price_series_show_requires_pipeline) rejects
  // show_prediction_in_app=TRUE while run_prediction_pipeline=FALSE. Mirror
  // it client-side: "Show forecasts in app" is locked unless the pipeline
  // is on; flipping the pipeline OFF auto-resets show so the form stays
  // submittable.
  const runValue = useWatch({ control, name: 'runPredictionPipeline' });
  const showLocked = !runValue;
  useEffect(() => {
    if (!runValue) {
      setValue('showPredictionInApp', false, { shouldDirty: false });
    }
  }, [runValue, setValue]);

  const submitting = isSubmitting || updateMutation.isPending;

  const onSubmit = async (values: PriceSeriesFormValues) => {
    if (!row) return;
    try {
      await updateMutation.mutateAsync({
        id: row.id,
        payload: {
          isActive: values.isActive,
          isManualMarketPrice: values.isManualMarketPrice,
          runPredictionPipeline: values.runPredictionPipeline,
          showPredictionInApp: values.showPredictionInApp,
          // Empty string is normalized to null server-side.
          notes: values.notes,
        },
      });
      onClose();
    } catch {
      // Toast already shown by the mutation's onError.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="price-series-edit-title"
    >
      <DialogTitle id="price-series-edit-title" sx={{ pb: 0.5 }}>
        Edit price series
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <>
            <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-surface-muted px-4 py-3 text-[12.5px]">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  APMC
                </div>
                <div className="font-medium text-foreground">{row.apmcName}</div>
                <div className="text-foreground-muted">
                  {row.apmcDistrictName} · {row.apmcStateName}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Crop
                </div>
                <div className="font-medium text-foreground">{row.cropName}</div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Variety
                </div>
                <div className="font-medium text-foreground">{row.varietyName}</div>
              </div>
            </div>

            <form
              id="price-series-edit-form"
              onSubmit={handleSubmit(onSubmit)}
              className="grid gap-3"
            >
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <ToggleRow
                    label="Active"
                    helper="When off: this series is not served, scraped, or predicted."
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                control={control}
                name="isManualMarketPrice"
                render={({ field }) => (
                  <ToggleRow
                    label="Manual market price"
                    helper="When on: each morning a draft observation row is pre-created for field agents to fill in. Scraping still runs but won't overwrite manual rows."
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                control={control}
                name="runPredictionPipeline"
                render={({ field }) => (
                  <ToggleRow
                    label="Run prediction pipeline"
                    helper="When on: the forecast model runs for this series. Costs compute time per run. Required prerequisite for showing forecasts in the app."
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                control={control}
                name="showPredictionInApp"
                render={({ field }) => (
                  <ToggleRow
                    label="Show forecasts in app"
                    helper={
                      showLocked
                        ? 'Disabled — turn "Run prediction pipeline" on first. Without it, mobile would render an empty predictions section (DB rejects this combination via chk_price_series_show_requires_pipeline).'
                        : 'When on: forecasts surface in the consumer app. The inverse (pipeline on, app off) is the silent backfill / model-validation mode.'
                    }
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={showLocked}
                  />
                )}
              />

              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Why this series was added/paused, source quirks, contact for manual entry, etc."
                error={!!errors.notes}
                helperText={errors.notes?.message ?? 'Optional · 2000 chars max.'}
                {...register('notes')}
              />
            </form>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="price-series-edit-form"
          variant="contained"
          color="primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
